import { createSelector } from '@ngrx/store';
import { AppState } from './store';
import { Study } from '../../shared/models/study.model';
import { ReportContentItemResponse } from '../../shared/models/run.model';
import { AdjustedCalFormat, CalculationResult, InstrumentStatus, RegressionStats, RunStatus } from '../../shared/models/result.model';
import { MeanBiasAndSlope } from '../../shared/models/cal-lot.model';
import { sortReportContentItemsForDisplay } from '../../shared/utils/run-info.utils';
import { DiscardWarningSummary, evaluateDiscardThresholds } from '../../shared/utils/discard-threshold.util';
import { isCalAdjustFinal } from '../../shared/utils/run-type.util';

// Builds a placeholder Study mirroring the fields our Run Info components expect. This lets tests bootstrap NgRx
// without a real study while still exercising the perf optimizations (memoized hydrations, cached seeds, etc.).
const createEmptyStudy = (): Study => {
  const study = new Study('');
  study.studyName = '';
  study.runType = '';
  study.panelSpecs = null;
  study.testSpec = '';
  study.assignSpec = '';
  study.refKitLot = '';
  study.patientFileMetadata = [];
  study.calFileMetadata = [];
  study.controlFileMetadata = [];
  study.runSamples = [];
  study.runSampleLots = [];
  study.reportContentItems = {
    variableColumns: [],
    reportContentItems: []
  } as ReportContentItemResponse;
  study.selectedCalcReportContentItemId = null;
  study.selectedSampleReportContentItemId = null;
  study.calcFailedSamplesMode = false;
  study.sampleFailedSamplesMode = false;
  study.selectedControlAnalysisReportContentItemId = null;
  study.selectedControlCurveReportContentItemId = null;
  study.selectedAssignmentReportContentItemId = null;
  study.calLots = [];
  study.adjustedCalLots = {
    adjustedCalLot: [],
    originalCalLot: [],
    valueAssignment: []
  } as AdjustedCalFormat;
  study.meanBiasAndSlope = {} as MeanBiasAndSlope;
  study.qcLots = [];
  study.kitLots = [];
  study.assayNames = [];
  study.beadRegions = [];
  study.calculationResponse = null;
  study.calculationAdjustedResponse = null;
  study.sampleDataOriginal = [];
  study.sampleDataAdjusted = [];
  study.sampleDataFailuresOriginal = [];
  study.sampleDataFailuresAdjusted = [];
  study.activeAssayTab = null;
  study.assaySummaryColumnSelection = [];
  study.assayDetailsColumnSelection = [];
  study.comments = '';
  study.calculate_averages = false;
  study.use_legacy_4pl = false;
  study.optimize_adjustment = false;
  study.skip_missing_samples = false;
  study.allowed_adjustment_factor = null;
  study.runDataPanels = null;
  study.isDraft = false;
  return study;
};

// Mirrors the structure NgRx holds in production so selectors (and the perf tweaks built on them) can run in tests.
const EMPTY_APP_STATE: AppState = {
  isAuthenticated: false,
  isProcessing: false,
  existingStudy: false,
  loggedInUser: '',
  loggedInUserEmail: '',
  appVersion: '',
  isSuperUser: false,
  authToken: '',
  selectedStudy: createEmptyStudy(),
  areStudyChangesPending: false,
  panelName: '',
  isFirstColumnReferenceLot: false,
  isCalculateEnabled: false,
  isCalculateAdjustedEnabled: false,
  calculationStatus: '',
  calAdjustmentCriteria: [],
  loadStudyError: null,
  preparedInstrumentReportSeeds: [],
  preparedRunSampleLots: [],
  sampleDataFresh: false,
  sampleDataLoading: false,
  sampleDataPendingLoads: null,
  lastCalculationId: null,
  lastCalculationType: null,
  lastUnadjustedCalculationId: null,
  lastAdjustedCalculationId: null,
  lastSavedComment: null,
  hasNewInstrumentUploads: false,
  adjustedCalibratorVisited: false,
  hasSeededAdjustedAssignment: false,
};
/**
 * selectAppState` selects the entire `AppState`.
 * Runtime code depends on this slice always existing (especially after the perf work that cached seeds in NgRx),
 * so we keep the type strict while still tolerating test stores that register the state either at the root or under `appState`.
 */
// Tests sometimes register the entire AppState at the root (instead of under appState), so this guard lets us accept either shape.
const hasAppStateShape = (state: unknown): state is AppState =>
  !!state &&
  typeof state === 'object' &&
  'selectedStudy' in (state as AppState) &&
  'preparedInstrumentReportSeeds' in (state as AppState);

export const selectAppState = createSelector(
  (state: { appState?: AppState } | undefined) => state,
  (state): AppState => {
    if (!state) {
      return EMPTY_APP_STATE;
    }
    if (state.appState) {
      return state.appState;
    }
    if (state.appState === null) {
      return EMPTY_APP_STATE;
    }
    if (hasAppStateShape(state)) {
      return state;
    }
    return EMPTY_APP_STATE;
  }
);
/**
 * `selectStudy` is a selector that extracts the `selectedStudy` property from the `AppState`.
 * Components rely on a concrete Study, so we return the store’s study directly; specs should populate it so the
 * tab-visibility and hydration optimizations can keep using memoized references just like production.
 */
export const selectStudy = createSelector(
  selectAppState,
  (state: AppState | null | undefined) => state?.selectedStudy ?? createEmptyStudy()
);
/**
 * `selectPanelName` is a selector that extracts the `panelName` from the `AppState`.
 * It provides the name of the currently selected panel (e.g., for a specific lot) so the tab gating logic can
 * still display headings instantly after the performance work detached change detection for hidden tabs.
 */
export const selectPanelName = createSelector(
  selectAppState,
  (state: AppState | null | undefined) => state?.panelName ?? ''
);

export const selectIsSuperUser = createSelector(
  selectAppState,
  (state: AppState | null | undefined) => !!state?.isSuperUser
);

export const selectStudyInstanceId = createSelector(
  selectAppState,
  (state: AppState | null | undefined) => state?.studyInstanceId ?? 0
);

/** True when the in-memory study differs from the copy persisted on the server. */
export const selectAreStudyChangesPending = createSelector(
  selectAppState,
  (state: AppState | null | undefined) => !!state?.areStudyChangesPending
);

/** True once the study has been saved to (or loaded from) the server; exports and print views need that copy. */
export const selectIsStudySaved = createSelector(
  selectAppState,
  (state: AppState | null | undefined) => !!state?.existingStudy
);

/**
 * Audit trail entries are only created server-side on save, so printing while edits are pending
 * would produce a record that does not match the persisted study.
 *
 * Gated on `existingStudy` rather than `studyId`: `studyId` carries the backend's BPX-YY-XXXX id,
 * which drafts never have, but a loaded draft is persisted and has an audit trail worth printing.
 */
export const selectCanPrintAuditTrail = createSelector(
  selectAppState,
  (state: AppState | null | undefined) =>
    !!state?.existingStudy && !state?.areStudyChangesPending
);
// Cached instrument seeds underpin the Report Items speedups, so selectors emit [] in tests to keep memoized hydrations happy.
export const selectPreparedInstrumentSeeds = createSelector(
  selectAppState,
  (state: AppState | null | undefined) => state?.preparedInstrumentReportSeeds ?? []
);

// Run Samples consumes precomputed lot structures; this selector always produces an array so the incremental hydration logic can diff efficiently.
export const selectPreparedRunSampleLots = createSelector(
  selectAppState,
  (state: AppState | null | undefined) => state?.preparedRunSampleLots ?? []
);

export const selectSampleDataFresh = createSelector(
  selectAppState,
  (state: AppState | null | undefined) => state?.sampleDataFresh ?? false
);

export const selectSampleDataLoading = createSelector(
  selectAppState,
  (state: AppState | null | undefined) => state?.sampleDataLoading ?? false
);

export const selectSampleDataStatus = createSelector(
  selectSampleDataFresh,
  selectSampleDataLoading,
  (sampleDataFresh, sampleDataLoading) => ({ sampleDataFresh, sampleDataLoading })
);

// Defaulting to [] keeps the virtualized Run Samples grid from rehydrating more rows than necessary during the new chunked sync.
export const selectRunSamplesSlice = createSelector(
  selectStudy,
  (study) => study.runSamples ?? []
);

// Report content is optional, so consumers get either the payload or null; this keeps the deferred Report Items hydrator aware of real changes only.
export const selectReportContentItemsSlice = createSelector(
  selectStudy,
  (study) => {
    const response = study.reportContentItems ?? null;
    if (!response) {
      return null;
    }
    const sortedItems = sortReportContentItemsForDisplay(response.reportContentItems, study.runType);
    return {
      ...response,
      reportContentItems: sortedItems
    };
  }
);

// Combines the slices Run Samples cares about so the component subscribes once, enabling the structural comparer that drives the new deferred hydration.
export const selectRunSamplesPayloadSlice = createSelector(
  selectRunSamplesSlice,
  selectReportContentItemsSlice,
  selectPreparedRunSampleLots,
  (runSamples, reportContentItems, preparedRunSampleLots) => ({
    runSamples: runSamples ?? [],
    reportContent: reportContentItems ?? null,
    runSampleLots: preparedRunSampleLots ?? []
  })
);

// Simple selectors so components can subscribe to the cached pull_sample_data arrays directly.
export const selectSampleDataOriginal = createSelector(
  selectStudy,
  study => study.sampleDataOriginal ?? []
);

export const selectSampleDataAdjusted = createSelector(
  selectStudy,
  study => study.sampleDataAdjusted ?? []
);

export const selectSampleDataFailuresOriginal = createSelector(
  selectStudy,
  study => study.sampleDataFailuresOriginal ?? []
);

export const selectSampleDataFailuresAdjusted = createSelector(
  selectStudy,
  study => study.sampleDataFailuresAdjusted ?? []
);

// Persisted column selections for assay export/print filters.
export const selectAssaySummaryColumnSelection = createSelector(
  selectStudy,
  study => study.assaySummaryColumnSelection ?? []
);

export const selectAssayDetailsColumnSelection = createSelector(
  selectStudy,
  study => study.assayDetailsColumnSelection ?? []
);

/**
 * Many feature modules only need small pieces of the Study object for binding.
 * These selectors expose those primitives directly so components can stay OnPush.
 */
export const selectPatientFileMetadata = createSelector(
  selectStudy,
  study => study.patientFileMetadata ?? []
);

export const selectPanelSpecsSlice = createSelector(
  selectStudy,
  study => study.panelSpecs ?? null
);

export const selectCalculationResult = createSelector(
  selectStudy,
  (study): CalculationResult | null => study.calculationResponse?.calculationResponse ?? null
);

export const selectCalculationAdjustedResult = createSelector(
  selectStudy,
  (study): CalculationResult | null => study.calculationAdjustedResponse?.calculationResponse ?? null
);

/** True when a Calibrator Adjustment study's results come from the single Final-mode run. */
export const selectIsCalAdjustFinal = createSelector(
  selectStudy,
  study => isCalAdjustFinal(study)
);

export const selectCalLotsSlice = createSelector(
  selectStudy,
  study => study.calLots ?? []
);

export const selectQcLotsSlice = createSelector(
  selectStudy,
  study => study.qcLots ?? []
);

export const selectRunType = createSelector(
  selectStudy,
  study => study.runType ?? ''
);

export const selectTestSpec = createSelector(
  selectStudy,
  study => study.testSpec ?? ''
);

export const selectRefKitLot = createSelector(
  selectStudy,
  study => study.refKitLot ?? ''
);

// Sample Errors screen payload so the component can consume one selector (avoids selector-combining warnings).
export const selectSampleErrorsPayload = createSelector(
  selectPanelName,
  selectRunType,
  selectTestSpec,
  selectRefKitLot,
  selectAppState,
  selectCalculationResult,
  selectCalculationAdjustedResult,
  selectReportContentItemsSlice,
  (
    panelName,
    runType,
    testSpec,
    refKitLot,
    appState,
    calculationResult,
    calculationAdjustedResult,
    reportContentResponse
  ) => ({
    panelName,
    runType,
    testSpec,
    refKitLot,
    appState,
    calculationResult,
    calculationAdjustedResult,
    reportContentResponse
  })
);

// QC Summary screen payload so the component can consume one selector (avoids selector-combining warnings).
export const selectQcSummaryPayload = createSelector(
  selectPanelName,
  selectRunType,
  selectTestSpec,
  selectRefKitLot,
  selectPatientFileMetadata,
  selectPanelSpecsSlice,
  selectCalLotsSlice,
  selectQcLotsSlice,
  selectReportContentItemsSlice,
  selectCalculationResult,
  selectCalculationAdjustedResult,
  selectAppState,
  selectIsCalAdjustFinal,
  (
    panelName,
    runType,
    testSpec,
    refKitLot,
    patientFileMetadata,
    panelSpecs,
    calLots,
    qcLots,
    reportContentResponse,
    calculationResult,
    calculationAdjustedResult,
    appState,
    isCalAdjustFinal
  ) => ({
    panelName,
    runType,
    testSpec,
    refKitLot,
    patientFileMetadata,
    panelSpecs,
    calLots,
    qcLots,
    reportContentResponse,
    calculationResult,
    calculationAdjustedResult,
    appState,
    isCalAdjustFinal
  })
);

// Status summary dialog payload (avoids combining selectors in the component).
export const selectStatusSummaryPayload = createSelector(
  selectReportContentItemsSlice,
  selectCalculationResult,
  selectCalculationAdjustedResult,
  selectRefKitLot,
  selectIsCalAdjustFinal,
  selectRunType,
  (reportContentResponse, calculationResult, calculationAdjustedResult, refKitLot, isCalAdjustFinal, runType) => ({
    reportContentResponse,
    calculationResult,
    calculationAdjustedResult,
    refKitLot,
    isCalAdjustFinal,
    runType
  })
);

// FR Report payload so the component can consume one selector (avoids selector-combining warnings).
export const selectFrReportPayload = createSelector(
  selectStudy,
  selectReportContentItemsSlice,
  selectAppState,
  (study, reportContentResponse, appState) => ({
    study,
    reportContentResponse,
    appState
  })
);

// Components toggle the heavy assay tabs based on the selected label, so expose just the string
// instead of forcing them to subscribe to the entire Study record.
export const selectActiveAssayTab = createSelector(
  selectStudy,
  study => study.activeAssayTab ?? null
);

export const selectCalculationRunStatus = createSelector(
  selectStudy,
  // Calculations tabs only need the runStatus array, so expose it directly to avoid
  // waking every consumer with the entire Study object whenever NgRx emits.
  (study): RunStatus[] => study.calculationResponse?.calculationResponse?.runStatus ?? []
);

export const selectCalculationRegressionStats = createSelector(
  selectStudy,
  // Same idea for regression stats: feed the component the exact slice that drives the grid.
  (study): RegressionStats[] => study.calculationResponse?.calculationResponse?.regressionStats ?? []
);

// Bundle sample failures payload to avoid combining selectors in components.
export const selectSampleFailuresPayload = createSelector(
  selectReportContentItemsSlice,
  selectSampleDataFailuresOriginal,
  selectCalculationRegressionStats,
  (reportContent, sampleFailures, regressionStats) => ({
    reportContent,
    sampleFailures,
    regressionStats
  })
);

export const selectSelectedCalcReportContentItemId = createSelector(
  selectStudy,
  study => study.selectedCalcReportContentItemId ?? null
);

export const selectCalcFailedSamplesMode = createSelector(
  selectStudy,
  study => study.calcFailedSamplesMode ?? false
);

export const selectSampleFailedSamplesMode = createSelector(
  selectStudy,
  study => study.sampleFailedSamplesMode ?? false
);

export const selectInstrumentStatus = createSelector(
  selectStudy,
  (study): InstrumentStatus[] => study.calculationResponse?.calculationResponse?.instrumentStatus ?? []
);

// Threshold warning payload. Unlike selectInstrumentStatus / selectCalculationRunStatus above, this
// falls back to the adjusted response when only it exists, matching how the Sample Errors screen picks
// its calculation result. Kept separate so those unadjusted-only selectors keep their semantics.
export const selectDiscardThresholdPayload = createSelector(
  selectCalculationResult,
  selectCalculationAdjustedResult,
  selectReportContentItemsSlice,
  (calculationResult, calculationAdjustedResult, reportContentResponse) => {
    const selectedResult = calculationResult ?? calculationAdjustedResult;
    return {
      instrumentStatus: selectedResult?.instrumentStatus ?? [],
      runStatus: selectedResult?.runStatus ?? [],
      reportContentItems: reportContentResponse?.reportContentItems ?? []
    };
  }
);

// The evaluated run-quality warning. Shared by the effect that raises the dialog and by the
// Instrument Errors tab, which restates the same figures so they survive the operator pressing OK.
export const selectDiscardWarningSummary = createSelector(
  selectDiscardThresholdPayload,
  (payload): DiscardWarningSummary | null => evaluateDiscardThresholds(
    payload.instrumentStatus,
    payload.runStatus,
    payload.reportContentItems
  )
);
